import { computed, ref, type ComputedRef } from 'vue'
import { intersection } from 'lodash-es'

type SorterFunction = (item: CanvasRetrieve) => string | number | boolean
type OrderDirection = 'asc' | 'desc'

import useRecommendations from '@canvas-builder/composables/useRecommendations'
import {
  SectionPopularityCategory,
  SectionTrendlineDirection,
  SystemFilter,
  type SectionExtra,
} from '@lib/types/sectionPopularity'
import type { CanvasRetrieve } from '@openapi'

const popularityDict = ref<Record<string, SectionExtra>>({})

const popularSections = computed(() =>
  Object.entries(popularityDict.value)
    .map(([key, value]) => ({
      id: key,
      ...value,
    }))
    .filter(
      (item) =>
        item.popularity.category === SectionPopularityCategory.POPULAR ||
        item.popularity.category === SectionPopularityCategory.THE_MOST_POPULAR,
    ),
)

const engagingSections = computed(() =>
  Object.entries(popularityDict.value)
    .map(([key, value]) => ({
      id: key,
      ...value,
    }))
    .filter((item) => item.engagement.direction === SectionTrendlineDirection.ASC),
)

function calculateTrendline(data: number[]): number {
  const N = data.length

  if (N <= 1) return 0

  let sumX = 0
  let sumY = 0
  let sumXY = 0
  let sumX2 = 0

  data.forEach((y, index) => {
    const x = index + 1

    sumX += x
    sumY += y
    sumXY += x * y
    sumX2 += x * x
  })

  const denominator = N * sumX2 - sumX * sumX
  if (denominator === 0) return 0

  const slope = (N * sumXY - sumX * sumY) / denominator
  return slope
}

function getPopularityCategory(value: number) {
  if (isNaN(value) || value < 1) return SectionPopularityCategory.NONE
  else return SectionPopularityCategory.POPULAR
}

export default function useSectionPopularity(filters?: ComputedRef<Record<string, any>>) {
  const { recommendedSectionIds } = useRecommendations()

  const isPopularFilterActive = computed(() =>
    filters ? Object.entries(filters.value).find(([key, value]) => key.includes(SystemFilter.POPULAR) && value) : false,
  )
  const isRecommendedFilterActive = computed(() =>
    filters
      ? Object.entries(filters.value).find(([key, value]) => key.includes(SystemFilter.RECOMMENDED) && value)
      : false,
  )
  const isLatestFilterActive = computed(() =>
    filters ? Object.entries(filters.value).find(([key, value]) => key.includes(SystemFilter.LATEST) && value) : false,
  )
  const isEngagingFilterActive = computed(() =>
    filters
      ? Object.entries(filters.value).find(([key, value]) => key.includes(SystemFilter.ENGAGING) && value)
      : false,
  )

  function getOrderingConfiguration({
    sortByMetadata,
    sequencingKey,
  }: {
    sortByMetadata?: boolean
    sequencingKey?: string
  } = {}) {
    const sorters: [SorterFunction, OrderDirection][] = []

    if (isPopularFilterActive.value) {
      sorters.push(
        [
          (item: CanvasRetrieve) => {
            const popularityItem = popularSections.value.find((section) => section.id === item.id)
            return popularityItem ? popularityItem.popularity?.value : 0
          },
          'desc',
        ],
        [
          (item: CanvasRetrieve) => {
            const popularityItem = popularSections.value.find((section) => section.id === item.id)
            return popularityItem ? popularityItem.views : 0
          },
          'desc',
        ],
      )
    } else if (isEngagingFilterActive.value) {
      sorters.push([
        (item: CanvasRetrieve) => {
          const engagementItem = engagingSections.value.find((section) => section.id === item.id)
          return engagementItem ? engagementItem.engagement?.trendline : 0
        },
        'desc',
      ])
    } else if (isLatestFilterActive.value) {
      sorters.push([(item: CanvasRetrieve) => item.modified_at, 'desc'])
    }

    // Add recommended sections on top only if no other system filters are applied
    if (!isPopularFilterActive.value && !isEngagingFilterActive.value && !isLatestFilterActive.value) {
      sorters.push([(item: CanvasRetrieve) => recommendedSectionIds.value.includes(item.id), 'desc'])
    }

    // Add the default sorting to all cases
    const sKey = sequencingKey || 'sequence'
    if (sortByMetadata)
      sorters.push(
        [(item: CanvasRetrieve) => item.metadata?.[sKey] == null, 'asc'],
        [(item: CanvasRetrieve) => (item.metadata?.[sKey] ? parseInt(item.metadata[sKey], 10) : 0), 'asc'],
      )

    return sorters
  }

  function getSystemFilterSectionIds(): string[] | null {
    const popularIds = isPopularFilterActive.value ? popularSections.value.map((item) => item.id) : undefined
    const recommendedIds = isRecommendedFilterActive.value ? recommendedSectionIds.value : undefined
    const engagingIds = isEngagingFilterActive.value ? engagingSections.value.map((item) => item.id) : undefined

    const filterArrays = [popularIds, recommendedIds, engagingIds].filter((arr) => arr)
    const intersectionIds = filterArrays.length > 0 ? intersection(...filterArrays) : []

    if (!filterArrays.length) return []
    if (filterArrays.length > 0 && !intersectionIds.length) return null
    return intersectionIds
  }

  function getDefaultOrdering(): string {
    const ordering: string[] = []
    if (isLatestFilterActive.value) {
      ordering.push('-modified_at')
    }
    return ordering.join(',') || 'name'
  }

  return {
    calculateTrendline,
    engagingSections,
    getOrderingConfiguration,
    getPopularityCategory,
    getSystemFilterSectionIds,
    isEngagingFilterActive,
    isLatestFilterActive,
    isPopularFilterActive,
    isRecommendedFilterActive,
    popularityDict,
    popularSections,
    getDefaultOrdering,
  }
}
